import { useCallback } from 'react';
import { StreamingDimensionResponse } from 'fym-shared/src/music24/streaming';
import { ArtistPortalStreamsByDimensionResponse } from 'fym-shared/src/smartlinks/artistPortals';
import { useGetDittoStreamsByDimensionQuery, useGetStreamingDimensionQuery } from '../../../../redux/api/fymAPI';

/** The answer of a store or country read. `sources` is there for the read of every source. */
export type StreamingDimensionAnswer = ArtistPortalStreamsByDimensionResponse &
  Partial<Pick<StreamingDimensionResponse, 'sources'>>;

/**
 * The streams of each store or country (design c). With no track, the read covers every connected
 * source (`streaming/stores|countries`): a direct portal is its own store, a distributor reports
 * many stores, and nothing is counted twice. With a track, the read is the distributor track
 * (`trackExternalId`). `skip` is true when no connected source gives the section: the screen asks
 * nothing.
 */
export function useStreamingDimension(input: {
  accountId: number;
  dimension: 'store' | 'country';
  days: number;
  trackExternalId?: string;
  skip: boolean;
}) {
  const { accountId, dimension, days, trackExternalId, skip } = input;
  const ofTrack = trackExternalId !== undefined;
  const all = useGetStreamingDimensionQuery({ accountId, dimension, days }, { skip: skip || ofTrack });
  const track = useGetDittoStreamsByDimensionQuery(
    { accountId, dimension, days, trackExternalId },
    { skip: skip || !ofTrack }
  );
  const read = ofTrack ? track : all;
  const { refetch } = read;
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  const data: StreamingDimensionAnswer | undefined = ofTrack ? track.data : all.data;
  return { data, isLoading: read.isLoading, error: read.error, retry };
}
